import { useRef, useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Premium interactive before/after drag slider.
 * - Thick red handle with chevron knob (design spec)
 * - Pointer + touch support
 * - Bilingual labels (Before/After or قبل/بعد)
 */
const BeforeAfterSlider = ({ beforeSrc, afterSrc, caseNumber }) => {
  const { t, isRTL } = useLanguage();
  const wrapRef = useRef(null);
  const [pct, setPct] = useState(50);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const raw = ((clientX - rect.left) / rect.width) * 100;
    setPct(Math.max(0, Math.min(100, raw)));
  }, []);

  const handlePointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };
  const handlePointerUp = (e) => {
    draggingRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // Keyboard accessibility
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (document.activeElement !== el) return;
      if (e.key === "ArrowLeft") setPct((p) => Math.max(0, p - 3));
      if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 3));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // In RTL we keep pixel-based slider behavior (pct still maps left->right visually)
  const clip = `inset(0 ${100 - pct}% 0 0)`;

  return (
    <div
      ref={wrapRef}
      className="dx-ba-wrap group"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      tabIndex={0}
      role="slider"
      aria-label="Before and After"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      data-testid={`before-after-slider-case-${caseNumber}`}
    >
      {/* Before image (full) */}
      <img
        src={beforeSrc}
        alt={`Case ${caseNumber} before`}
        className="dx-ba-img"
        draggable={false}
      />
      {/* After image clipped */}
      <div className="dx-ba-after-wrap" style={{ clipPath: clip, WebkitClipPath: clip }}>
        <img
          src={afterSrc}
          alt={`Case ${caseNumber} after`}
          className="dx-ba-img"
          draggable={false}
        />
      </div>

      {/* Labels — fixed to physical sides because the slider's clip-path
          direction is identical in LTR and RTL. */}
      <div className="dx-ba-label font-display" style={{ left: 14 }}>
        {t.gallery.before}
      </div>
      <div className="dx-ba-label font-display" style={{ right: 14 }}>
        {t.gallery.after}
      </div>

      {/* Drag hint */}
      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[3] text-[10px] tracking-[0.25em] uppercase text-white/70 bg-black/50 border border-white/10 rounded-full px-3 py-1 backdrop-blur pointer-events-none opacity-80 group-hover:opacity-100 transition"
      >
        {t.gallery.dragHint}
      </div>

      {/* Handle */}
      <div className="dx-ba-handle" style={{ left: `${pct}%` }}>
        <div className="dx-ba-knob" aria-hidden>
          <ChevronLeft strokeWidth={3} />
          <ChevronRight strokeWidth={3} />
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
